import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';

import Button from './UIKit/Button';
import CustomIframe from './UIKit/CustomIframe';

const ResponsivePreviewModal = ({ open, onClose, lesson, title, selectedBlock }) => {
  const { t } = useTranslation(['lessons']);
  const theme = useTheme();
  const [previewMode, setPreviewMode] = useState('desktop');

  const PREVIEW_WIDTHS = {
    desktop: {
      label: t('lessons:editor.html.desktop'),
      width: '100%',
      maxWidth: 1344,
    },
    tablet: { 
      label: t('lessons:editor.html.tablet'), 
      width: 768,
      maxWidth: 768 
    },
    mobile: { 
      label: t('lessons:editor.html.mobile'), 
      width: 375,
      maxWidth: 375 
    },
  };

  const handlePreviewMode = (event, newMode) => {
    if (newMode) {
      setPreviewMode(newMode);
    }
  };

  const handleClose = () => {
    setPreviewMode('desktop'); // Reset to default mode
    onClose();
  };

  const previewStyle = {
    width: typeof PREVIEW_WIDTHS[previewMode].width === 'number' 
      ? PREVIEW_WIDTHS[previewMode].width 
      : '100%',
    maxWidth: PREVIEW_WIDTHS[previewMode].maxWidth,
    margin: '0 auto',
  };

  // Function to render individual content block
  const renderContentBlock = (block) => {
    if (!block) return null;

    switch (block.type) {
      case 'presentation':
      case 'htmlBlock':
        return (
          <Box sx={{ 
            mb: 3, 
            width: '100%',
            maxHeight: '600px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.5)',
              },
            },
          }}>
            <iframe
              srcDoc={block.content || ''}
              title="Presentation Preview"
              style={{
                width: '100%',
                minHeight: '600px',
                border: 'none',
                borderRadius: '0',
                backgroundColor: 'transparent'
              }}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
            />
          </Box>
        );
      case 'fullHtml':
        return (
          <Box sx={{ 
            mb: 3, 
            width: '100%',
            maxHeight: '600px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.5)',
              },
            },
          }}>
            <CustomIframe
              srcDoc={block.content || block.htmlContent || block.html || ''}
              title="Custom HTML Preview"
              style={{
                width: '100%',
                minHeight: '600px',
                border: 'none',
                borderRadius: '0',
                backgroundColor: 'transparent',
                overflow: 'auto'
              }}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
            />
          </Box>
        );
      
      case 'standard':
      case 'text':
      default:
        return (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {block.content}
            </Typography>
          </Box>
        );
    }
  };

  const renderContent = () => {
    // If a specific block is selected, render only that block
    if (selectedBlock) {
      return renderContentBlock(selectedBlock);
    }

    // If no block is selected, don't show anything
    if (!lesson) return null;

    // Fallback: render the entire lesson (for backward compatibility)
    switch (lesson.contentType) {
      case 'presentation':
      case 'htmlBlock':
        return (
          <Box sx={{ 
            mb: 3, 
            width: '100%',
            maxHeight: '600px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.5)',
              },
            },
          }}>
            <iframe
            srcDoc={lesson.content || ''}
            title="HTML Preview"
            style={{
              width: '100%',
              minHeight: '600px',
              border: 'none',
              borderRadius: '0',
            }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
          />
          </Box>
        );
      case 'fullHtml':
        return (
          <Box sx={{ 
            mb: 3, 
            width: '100%',
            maxHeight: '600px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.5)',
              },
            },
          }}>
            <CustomIframe
              srcDoc={lesson.content || ''}
              title="Full HTML Preview"
              style={{
                width: '100%',
                minHeight: '600px',
                border: 'none',
                borderRadius: '0',
                backgroundColor: 'transparent',
                overflow: 'auto'
              }}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
            />
          </Box>
        );
      
      case 'video':
        return (
          <Box sx={{ mb: 3 }}>
            {(() => {
              // Функция для получения встраиваемого URL на основе обычного URL
              const getEmbedUrl = (url) => {
                if (!url) return '';

                // YouTube
                const youtubeRegex =
                  /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;
                const youtubeMatch = url.match(youtubeRegex);
                if (youtubeMatch) {
                  return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
                }

                // Vimeo
                const vimeoRegex = /(?:vimeo\.com\/)(\d+)/i;
                const vimeoMatch = url.match(vimeoRegex);
                if (vimeoMatch) {
                  return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
                }

                // Если не удалось преобразовать URL, возвращаем исходный
                return url;
              };

              // Проверяем, является ли URL действительным URL видео
              const isValidVideoUrl = (url) => {
                if (!url) return false;
                return /youtube\.com|youtu\.be|vimeo\.com|mp4$|webm$|ogg$/i.test(url);
              };

              const videoUrl = lesson.videoUrl || lesson.content;
              const embedUrl = getEmbedUrl(videoUrl);

              if (!isValidVideoUrl(videoUrl)) {
                return (
                  <Box sx={{ 
                    width: '100%', 
                    height: '400px', 
                    bgcolor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      Video: {videoUrl}
                    </Typography>
                  </Box>
                );
              }

              return (
                <Box sx={{ position: 'relative', width: '100%', pb: '56.25%', height: 0 }}>
                  <iframe
                    src={embedUrl}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: '16px',
                    }}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Video Preview"
                  />
                </Box>
              );
            })()}
          </Box>
        );
      
      case 'mixed':
        if (lesson.contentBlocks && lesson.contentBlocks.length > 0) {
          return (
            <Box>
              {lesson.contentBlocks.map((block, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  {renderContentBlock(block)}
                </Box>
              ))}
            </Box>
          );
        }
        return (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {lesson.content}
          </Typography>
        );
      
      default:
        return (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {lesson.content || ''}
          </ReactMarkdown>
        );
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          borderRadius: '16px',
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Typography variant="h6" component="div">
          {title || 'Responsive Preview'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Кнопки для перемикання режимів прев'ю */}
        <Box sx={{ 
          p: 2, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'center',
          gap: 1
        }}>
          <ToggleButtonGroup
            value={previewMode}
            exclusive
            onChange={handlePreviewMode}
            size="small"
          >
            {Object.entries(PREVIEW_WIDTHS).map(([mode, config]) => (
              <ToggleButton key={mode} value={mode} sx={{ minWidth: '80px' }}>
                {config.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Контент з прев'ю */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          maxHeight: 'calc(90vh - 140px)',
        }}>
          <Box sx={previewStyle}>
            {renderContent()}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={handleClose} variant="outlined">
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResponsivePreviewModal;
